// Dashboard.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Zap, Bookmark, BarChart2, Play } from 'lucide-react';
import '../../styles/Dashboard.css';
import StreakUpgradeButton from '../../components/StreakUpgradeButton';
import FreeMockExamCompletedModal from '../../components/modals/FreeMockExamCompletedModal';
import PremiumMockExamCompletedModal from '../../components/modals/PremiumMockExamCompletedModal';
import PricingModal from '../../components/modals/PricingModal'
import SelectTargetLevelModal from '../../components/modals/SelectTargetLevelModal';
import { API_URL } from '../../app/constants'

const DashboardView = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState({
    name: '',
    email: '',
    isPremium: false,
    currentLevel: ''
  });
  const [isUserLoaded, setIsUserLoaded] = useState(false);
  const [showMockExamCompletedModal, setShowMockExamCompletedModal] = useState(false);
  const [showPremiumMockExamCompletedModal, setShowPremiumMockExamCompletedModal] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [showMockExamIntroModal, setShowMockExamIntroModal] = useState(false);
  const [showSelectLevelModal, setShowSelectLevelModal] = useState(false);
  const [currentExams, setCurrentExams] = useState([]);
  const [currentStageIndex, setCurrentStageIndex] = useState(0);
  const [stagesData, setStagesData] = useState([]);
  
  // No unused state variables
  
  useEffect(() => {
    // Fetch user data and exam data
    const fetchData = async () => {
      try {
        const jwt = localStorage.getItem('jwt');
        
        // Fetch user data
        const userResponse = await fetch(`${API_URL}/api/v1/auth/me`, {
          headers: { 'Authorization': `Bearer ${jwt}` },
          method: "GET",
        });
        
        if (userResponse.ok) {
          const userData = await userResponse.json();
          const isPremium = userData.status === 'active';
          const currentLevel = userData.target_level || userData.level || '';
          
          setUser(prev => ({
            ...prev,
            name: userData.name || userData.email.split('@')[0],
            email: userData.email,
            isPremium: isPremium,
            currentLevel: currentLevel
          }));
          
          // Show level selection modal if current_level is empty
          if (!currentLevel || currentLevel === '') {
            setShowSelectLevelModal(true);
          }
          // Petit délai pour une transition plus naturelle
          setTimeout(() => {
            setIsUserLoaded(true);
          }, 100);
          
          // Check if user has completed their free mock exam
          try {
            const mockExamResponse = await fetch(`${API_URL}/api/v1/mock-exam/analytics`, {
              headers: { 'Authorization': `Bearer ${jwt}` }
            });
            
            if (mockExamResponse.ok) {
              // If we get 200 response, it means they've completed at least one mock exam
              const mockExamData = await mockExamResponse.json();
              console.log('User has completed mock exam:', mockExamData);
            } else if (mockExamResponse.status === 404) {
              // 404 means no completed mock exams found - user hasn't completed free mock exam yet
              console.log('User has not completed any mock exams yet');
            } else {
              // Other errors - assume no mock exam completed
              console.log('Error checking mock exam status, assuming not completed');
            }
          } catch (error) {
            console.log('Error fetching mock exam data - assuming user has not completed free mock exam yet:', error);
          }
          
          // Fetch current stage and exams
          try {
            const stagesResponse = await fetch(`${API_URL}/api/v1/panel/student/stages`, {
              headers: { 'Authorization': `Bearer ${jwt}` }
            });
            
            if (stagesResponse.ok) {
              const stagesDataResponse = await stagesResponse.json();
              console.log('Dashboard stages data:', stagesDataResponse);
              
              // Handle new API response structure
              const actualStages = stagesDataResponse.stages || stagesDataResponse;
              const needsDiagnostic = stagesDataResponse.needs_diagnostic || false;
              const apiCurrentLevel = stagesDataResponse.current_level;
              
              // If user needs diagnostic test, create diagnostic stage like TrainingView
              let displayStages = actualStages;
              if (needsDiagnostic && apiCurrentLevel && actualStages.length === 0) {
                displayStages = [{
                  stage_id: 0,
                  order: 0,
                  is_completed: false,
                  stage_type: "Diagnostic",
                  name: "Diagnostic Test",
                  session_explanation: "Take your diagnostic test to get personalized training sessions.",
                  exams: [{
                    exam_id: `diagnostic-${apiCurrentLevel}`,
                    section: "diagnostic",
                    completed: false,
                    score: null,
                    is_final_exam: false
                  }]
                }];
                console.log(`Created diagnostic stage for Dashboard level ${apiCurrentLevel}`);
              }
              
              setStagesData(displayStages);
              
              // Find current stage (first incomplete stage)
              const currentStageIdx = displayStages.findIndex(stage => !stage.is_completed);
              const finalStageIndex = currentStageIdx >= 0 ? currentStageIdx : 0;
              setCurrentStageIndex(finalStageIndex);
              const current = displayStages[finalStageIndex];
              
              if (current) {
                console.log('Dashboard current stage:', current);
                
                // Special handling for diagnostic stage (created artificially)
                if (current.stage_id === 0 && current.exams) {
                  // Use exams from the artificial diagnostic stage
                  const incompleteExams = current.exams.filter(exam => !exam.completed).slice(0, 2);
                  console.log('Dashboard diagnostic exams:', incompleteExams);
                  setCurrentExams(incompleteExams);
                } else {
                  // Fetch exams for regular stages
                  const examsResponse = await fetch(`${API_URL}/api/v1/panel/student/stage/${current.stage_id}/exams`, {
                    headers: { 'Authorization': `Bearer ${jwt}` }
                  });
                  
                  if (examsResponse.ok) {
                    const examsData = await examsResponse.json();
                    console.log('Dashboard exams data:', examsData);
                    
                    // Filter for incomplete exams and take first 2
                    const incompleteExams = examsData.filter(exam => !exam.completed).slice(0, 2);
                    console.log('Dashboard incomplete exams:', incompleteExams);
                    setCurrentExams(incompleteExams);
                  }
                }
              }
            }
          } catch (error) {
            console.error('Error fetching current exams:', error);
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, []);

  // Check if a stage/exam is accessible for the current user
  const isStageAccessible = (stageIndex) => {
    // Always allow diagnostic test (stage 0) and first stage (stage 1)
    if (stageIndex <= 1) return true;
    
    // For premium users, allow all stages
    if (user.isPremium) return true;
    
    // For free users, only allow if previous stage is completed
    if (stageIndex > 0 && stagesData[stageIndex - 1]?.is_completed) {
      // But still limit free users to first few stages (can be configured)
      return stageIndex <= 2; // Allow up to stage 2 for free users
    }
    
    return false;
  };

  // Handle exam click with permission check
  const handleExamClick = (exam, stageIndex) => {
    if (isStageAccessible(stageIndex)) {
      // Check if it's the diagnostic stage (first stage)
      if (stageIndex === 0) {
        // Map user level to diagnostic exam IDs (110-114)
        const levelToExamId = {
          'N5': 110,
          'N4': 111,
          'N3': 112,
          'N2': 113,
          'N1': 114
        };
        
        const diagnosticExamId = levelToExamId[user.currentLevel] || 110; // Default to N5
        navigate(`/diagnostic-exam/${diagnosticExamId}`);
      } else {
        // For other stages, go to personalized exam
        navigate(`/personalized-exam/${exam.exam_id}`);
      }
    } else {
      // Show upgrade modal for locked content
      setShowPricingModal(true);
    }
  };

  return (
    <div className="dashboard">
      <StreakUpgradeButton source="Dashboard" />
      <h1 className="dashboard-header">
        {isUserLoaded ? `Hello, ${user.name}!` : 'Hello!'}
      </h1>

      {/* Training and Practice Mode Sections */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '24px',
        marginBottom: '32px'
      }} className="dashboard-sections">
        {/* My Training Section */}
        <div>
          <div 
            style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              marginBottom: '16px'
            }}
            onClick={() => navigate('/study_plan')}
          >
            <span style={{
              color: '#9333EA',
              fontSize: '16px',
              fontWeight: '600',
              fontFamily: 'Manrope, sans-serif'
            }}>
              My Training
            </span>
            <span style={{
              color: '#9333EA',
              marginLeft: '8px',
              fontSize: '18px'
            }}>
              &gt;
            </span>
          </div>
            {currentExams.length > 0 ? currentExams.slice(0, 1).map((exam, index) => (
            <div
              key={exam.exam_id}
              onClick={() => handleExamClick(exam, currentStageIndex)}
              className="exercise-card"
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#9333EA';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#E5E7EB';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <div style={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                gap: '16px'
              }}>
                <div style={{ marginTop: 'auto' }}>
                  <span style={{
                    display: 'block',
                    fontFamily: 'Manrope, sans-serif',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#1F2937',
                    marginBottom: '8px'
                  }}>
                    {exam.section ? exam.section.charAt(0).toUpperCase() + exam.section.slice(1) : 'Practice'}
                  </span>
                  <h3 style={{
                    fontFamily: 'Manrope, sans-serif',
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#1F2937',
                    lineHeight: '1.4',
                    margin: '0',
                    textDecoration: 'none'
                  }}>
                    {exam.categories || exam.exam_name || `Exam ${index + 1}`}
                  </h3>
                </div>
                <div style={{
                  height: '4px',
                  background: '#F3F4F6',
                  borderRadius: '2px',
                  overflow: 'hidden',
                  width: '100%'
                }}>
                  <div style={{
                    height: '100%',
                    background: '#9334E9',
                    borderRadius: '2px',
                    transition: 'width 0.2s ease',
                    width: exam.completed ? '100%' : '0%'
                  }} />
                </div>
              </div>
            </div>
          )) : (
            // Skeleton card while loading
            <div className="exercise-card" style={{ cursor: 'default' }}>
              <div style={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                gap: '16px'
              }}>
                <div style={{ marginTop: 'auto' }}>
                  <div style={{
                    height: '14px',
                    width: '60px',
                    backgroundColor: '#F3F4F6',
                    borderRadius: '4px',
                    marginBottom: '8px',
                    animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                  }} />
                  <div style={{
                    height: '20px',
                    width: '80%',
                    backgroundColor: '#F3F4F6',
                    borderRadius: '4px',
                    animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                  }} />
                </div>
                <div style={{
                  height: '4px',
                  background: '#F3F4F6',
                  borderRadius: '2px',
                  width: '100%'
                }} />
              </div>
            </div>
            )}
        </div>

        {/* Practice Mode Section */}
        <div>
          <div 
            style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              marginBottom: '16px'
            }}
            onClick={() => navigate('/practice')}
          >
            <span style={{
              color: '#9333EA',
              fontSize: '16px',
              fontWeight: '600',
              fontFamily: 'Manrope, sans-serif'
            }}>
              Practice now
            </span>
            <span style={{
              color: '#9333EA',
              marginLeft: '8px',
              fontSize: '18px'
            }}>
              &gt;
            </span>
          </div>
          <div 
            className="practice-mode-card"
            onClick={() => navigate('/practice')}
            style={{
              background: '#F3E8FF',
              borderRadius: '16px',
              padding: '24px',
              color: '#7C3AED',
              cursor: 'pointer',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              minHeight: '160px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
              position: 'relative'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 8px 25px rgba(147, 51, 234, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <div style={{
              position: 'absolute',
              top: '24px',
              left: '24px',
              zIndex: 1
            }}>
              <Zap size={20} style={{ color: '#7C3AED' }} fill="#7C3AED" />
            </div>
            <div style={{ marginTop: 'auto' }}>
              <h2 style={{ 
                fontSize: '16px', 
                fontWeight: '700', 
                margin: '0 0 8px 0',
                fontFamily: 'Manrope, sans-serif',
                lineHeight: '1.2',
                color: '#7C3AED',
                textAlign: 'left'
              }}>
                Choose what to study!
              </h2>
              <p style={{ 
                fontSize: '13px', 
                margin: '0', 
                fontFamily: 'Manrope, sans-serif',
                lineHeight: '1.4',
                color: '#7C3AED',
                textAlign: 'left'
              }}>
                Let your analytics show you where to improve and train with purpose!
              </p>
            </div>
          </div>
        </div>
      </div>


      <h2 className="section-heading" style={{ marginTop: '32px', fontSize: '18px', fontWeight: '700', fontFamily: 'Manrope, sans-serif' }}>
        My Study Space
      </h2>

      <div className="dashboard-cards-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
        {/* Bookmarked Questions Card */}
        <div
          className="dashboard-card study-space-card"
          onClick={() => navigate('/saved')}
          style={{
            background: '#F3E8FF',
            borderRadius: '16px',
            padding: '24px',
            color: '#7C3AED',
            cursor: 'pointer',
            minHeight: '160px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            position: 'relative'
          }}
        >
          <div style={{
            position: 'absolute',
            top: '24px',
            left: '24px',
            zIndex: 1
          }}>
            <Bookmark size={20} style={{ color: '#7C3AED' }} fill="#7C3AED" />
          </div>
          <div style={{ marginTop: 'auto' }}>
            <h3 style={{ 
              fontSize: '16px', 
              fontWeight: '700', 
              margin: '0 0 8px 0',
              fontFamily: 'Manrope, sans-serif',
              color: '#7C3AED',
              textAlign: 'left',
              display: 'block'
            }}>
              Bookmarked Questions
            </h3>
            <p style={{ 
              fontSize: '13px', 
              margin: '0', 
              fontFamily: 'Manrope, sans-serif',
              lineHeight: '1.4',
              color: '#7C3AED',
              textAlign: 'left',
              display: 'block'
            }}>
              Quickly access the questions you've saved to review or study later.
            </p>
          </div>
        </div>

        {/* My Progress Card */}
        <div
          className="dashboard-card study-space-card"
          onClick={() => navigate('/stats')}
          style={{
            background: '#F3E8FF',
            borderRadius: '16px',
            padding: '24px',
            color: '#7C3AED',
            cursor: 'pointer',
            minHeight: '160px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            position: 'relative'
          }}
        >
          <div style={{
            position: 'absolute',
            top: '24px',
            left: '24px',
            zIndex: 1
          }}>
            <BarChart2 size={20} style={{ color: '#7C3AED' }} fill="#7C3AED" />
          </div>
          <div style={{ marginTop: 'auto' }}>
            <h3 style={{ 
              fontSize: '16px', 
              fontWeight: '700', 
              margin: '0 0 8px 0',
              fontFamily: 'Manrope, sans-serif',
              color: '#7C3AED',
              textAlign: 'left',
              display: 'block'
            }}>
              My Progress
            </h3>
            <p style={{ 
              fontSize: '13px', 
              margin: '0', 
              fontFamily: 'Manrope, sans-serif',
              lineHeight: '1.4',
              color: '#7C3AED',
              textAlign: 'left',
              display: 'block'
            }}>
              Track your performance, spot your weak areas, and guide your training with personalized insights.
            </p>
          </div>
        </div>

        {/* Video Import Card */}
        <div
          className="dashboard-card study-space-card"
          onClick={() => navigate('/contests')}
          style={{
            background: '#F3E8FF',
            borderRadius: '16px',
            padding: '24px',
            color: '#7C3AED',
            cursor: 'pointer',
            minHeight: '160px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            position: 'relative'
          }}
        >
          <div style={{
            position: 'absolute',
            top: '24px',
            left: '24px',
            zIndex: 1
          }}>
            <Play size={20} style={{ color: '#7C3AED' }} />
          </div>
          <div style={{ marginTop: 'auto' }}>
            <h3 style={{ 
              fontSize: '16px', 
              fontWeight: '700', 
              margin: '0 0 8px 0',
              fontFamily: 'Manrope, sans-serif',
              color: '#7C3AED',
              textAlign: 'left',
              display: 'block'
            }}>
              Import Your Videos
            </h3>
            <p style={{ 
              fontSize: '13px', 
              margin: '0', 
              fontFamily: 'Manrope, sans-serif',
              lineHeight: '1.4',
              color: '#7C3AED',
              textAlign: 'left',
              display: 'block'
            }}>
              Upload any Japanese video and get personalized quizzes to improve your listening skills.
            </p>
          </div>
        </div>
      </div>

      {/* Free Mock Exam Completed Modal */}
      <FreeMockExamCompletedModal 
        isOpen={showMockExamCompletedModal}
        onClose={() => setShowMockExamCompletedModal(false)}
        onUpgrade={() => setShowPricingModal(true)}
        source="Dashboard - Free Mock Exam"
      />

      {/* Premium Mock Exam Completed Modal */}
      <PremiumMockExamCompletedModal 
        isOpen={showPremiumMockExamCompletedModal}
        onClose={() => setShowPremiumMockExamCompletedModal(false)}
      />

      {/* Pricing Modal */}
      <PricingModal 
        isOpen={showPricingModal}
        onClose={() => setShowPricingModal(false)}
        source="Dashboard - Mock Exam Upgrade"
      />

      {/* Select Target Level Modal */}
      {showSelectLevelModal && (
        <SelectTargetLevelModal 
          onLevelSelect={(level) => {
            setUser(prev => ({...prev, currentLevel: level}));
            setShowSelectLevelModal(false);
          }}
        />
      )}

      {/* Mock Exam Introduction Modal */}
      {showMockExamIntroModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          backdropFilter: 'blur(2px)'
        }} onClick={() => setShowMockExamIntroModal(false)}>
          <div style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '16px',
            padding: '32px 28px',
            maxWidth: '450px',
            width: '90%',
            textAlign: 'center',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            position: 'relative'
          }} onClick={e => e.stopPropagation()}>
            <h2 style={{
              color: "#111827", 
              marginBottom: "16px",
              fontSize: '22px',
              fontWeight: '700',
              fontFamily: 'Manrope, sans-serif'
            }}>
              Full Mock Exam
            </h2>
            <p style={{
              margin: '0 0 24px 0',
              fontSize: '14px',
              lineHeight: '1.5',
              color: '#6B7280',
              fontFamily: 'Manrope, sans-serif',
              textAlign: 'center'
            }}>
              This exam replicates the structure of the official JLPT exam (without the listening section) and will take around 50 min to complete. Good luck!
            </p>

            <div style={{
              display: 'flex',
              gap: '12px',
              flexDirection: 'column'
            }}>
              <button
                style={{
                  backgroundColor: "#9334E9",
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: '600',
                  fontFamily: 'Manrope, sans-serif',
                  cursor: 'pointer',
                  width: '100%'
                }}
                onClick={() => {
                  setShowMockExamIntroModal(false);
                  navigate(`/mock-exam/N5`);
                }}
              >
                Start Mock Exam
              </button>
              <button
                style={{
                  backgroundColor: 'transparent',
                  color: '#6B7280',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: '500',
                  fontFamily: 'Manrope, sans-serif',
                  cursor: 'pointer',
                  width: '100%'
                }}
                onClick={() => setShowMockExamIntroModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardView;